import { useState } from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Typography from '@mui/material/Typography'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Markdown from '../Markdown'
import { bodyText, colours } from '../player/playerStyles'
import useBlockState from './useBlockState'

const RevealCards = ({ block, initialState, onStateChange }) => {
  const [state, update] = useBlockState({ revealed: [], ...initialState }, onStateChange)
  const revealed = state.revealed ?? []
  // Cards opened before stay open on a return visit; a card can be closed
  // again without losing its "opened" tick.
  const [open, setOpen] = useState(() => new Set(revealed))

  const toggle = (cardId) => {
    setOpen((previous) => {
      const next = new Set(previous)
      if (next.has(cardId)) next.delete(cardId)
      else next.add(cardId)
      return next
    })
    if (!revealed.includes(cardId)) update({ ...state, revealed: [...revealed, cardId] })
  }

  return (
    <Box>
      {block.intro && <Markdown sx={{ ...bodyText, mb: 2 }}>{block.intro}</Markdown>}
      <Typography aria-live="polite" sx={{ fontSize: '1rem', fontWeight: 700, color: colours.muted, mb: 1.5 }}>
        {revealed.length} of {block.cards.length} opened
      </Typography>
      <Box
        component="ul"
        sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, alignItems: 'start' }}
      >
        {block.cards.map((card) => {
          const isOpen = open.has(card.id)
          const wasOpened = revealed.includes(card.id)
          const panelId = `${block.id}-${card.id}-back`
          return (
            <Box
              component="li"
              key={card.id}
              sx={{ border: `2px solid ${wasOpened ? colours.good : colours.border}`, borderRadius: 1.5, bgcolor: '#FFFFFF', overflow: 'hidden' }}
            >
              <ButtonBase
                onClick={() => toggle(card.id)}
                aria-expanded={isOpen}
                aria-controls={panelId}
                sx={{
                  width: '100%',
                  minHeight: 64,
                  p: 1.75,
                  gap: 1,
                  justifyContent: 'space-between',
                  textAlign: 'left',
                  fontSize: '1.0625rem',
                  fontWeight: 700,
                  lineHeight: 1.4,
                  color: colours.text,
                  bgcolor: wasOpened ? colours.goodSoft : colours.accentSoft,
                }}
              >
                <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                  {card.front}
                  {wasOpened && <Box component="span" sx={{ fontWeight: 400, color: colours.good }}> (opened)</Box>}
                </Box>
                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  {wasOpened && <CheckCircleIcon sx={{ color: colours.good }} aria-hidden="true" />}
                  {isOpen ? <ExpandLessIcon aria-hidden="true" /> : <ExpandMoreIcon aria-hidden="true" />}
                </Box>
              </ButtonBase>
              <Box id={panelId} hidden={!isOpen} sx={{ p: 1.75, borderTop: `1px solid ${colours.hairline}` }}>
                <Markdown>{card.back}</Markdown>
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default RevealCards
